import { difference, isEqual } from 'lodash';

import { formatDateShort } from 'src/utils/helpers/transformDate';
import {
	getCompanyAreaLabel,
	pluralizeAreas,
} from 'src/utils/mappers/eventForm/area.mapper';
import { getStatusReasonLabel } from 'src/utils/mappers/companyStatus.mapper';

const SECTION_TITLES = {
	status: 'Статус',
	reasons: 'Основание',
	objections: 'Возражения',
	contacts: 'Контакты',
	documents: 'Документы',
	company_params: 'Параметры площадки',
	company_group_links: 'Связанные площадки',
	worths: 'Ценность',
};

const CHIP = {
	created: { label: 'Создано', color: 'success' },
	added: { label: 'Добавлено', color: 'success' },
	removed: { label: 'Снято', color: 'error' },
	deleted: { label: 'Удалено', color: 'error' },
	changed: { label: 'Изменено', color: 'warning' },
	uploaded: { label: 'Загружено', color: 'success' },
};

const CONTACT_STATUS_LABEL = {
	true: 'Работает',
	false: 'Больше не работает',
};

const parseJson = (value) => {
	if (!value) return null;
	if (typeof value === 'string') {
		try {
			return JSON.parse(value);
		} catch {
			return null;
		}
	}

	return value;
};

const normalizeEnum = (value) => {
	if (!value) return '';

	if (typeof value === 'object' && value.value) {
		return String(value.value).toLowerCase();
	}

	return String(value).toLowerCase();
};

const formatKnownFromDate = (value) => {
	if (!value) return '';

	const date = new Date(value.includes('T') ? value : `${value}T00:00:00`);

	return date.toLocaleDateString('ru-RU', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	});
};

const getStatusName = (statusById, statusId) => {
	if (!statusId) return null;

	return statusById?.[statusId]?.name ?? null;
};

const getObjectionTypeName = (objectionTypeById, typeId) => {
	if (!typeId) return 'Возражение';

	return objectionTypeById?.[typeId]?.name ?? 'Возражение';
};

const indexItemsByEntityType = (items = []) => {
	const map = new Map();

	for (const item of items) {
		const entityType = normalizeEnum(item.entityType);
		const list = map.get(entityType) ?? [];
		list.push({
			...item,
			entityType,
			role: normalizeEnum(item.role),
			operationType: normalizeEnum(item.operationType),
			oldValues: parseJson(item.oldValues),
			newValues: parseJson(item.newValues),
			payloadJson: parseJson(item.payloadJson),
		});
		map.set(entityType, list);
	}

	return map;
};

const ensureSection = (sections, key) => {
	let section = sections.find((entry) => entry.id === key);

	if (!section) {
		section = {
			id: key,
			title: SECTION_TITLES[key] ?? key,
			typos: [],
			reasons: [],
			status: null,
		};
		sections.push(section);
	}

	return section;
};

const mapStatusSection = (payload, itemsByType, context) => {
	const statusItem = itemsByType.get('companies')?.[0];
	const oldStatusId =
		statusItem?.oldValues?.statusId ?? statusItem?.oldValues?.status_id;
	const newStatusId =
		payload?.companyStatusId ??
		statusItem?.newValues?.statusId ??
		statusItem?.newValues?.status_id;
	const postponedUntil =
		payload?.statusPostponedUntil ??
		statusItem?.newValues?.statusPostponedUntil;

	if (!newStatusId && !postponedUntil) return null;

	let text =
		getStatusName(context.statusById, newStatusId) ?? 'Без изменений';
	let cross = getStatusName(context.statusById, oldStatusId);

	if (postponedUntil) {
		text = `Отложено до ${formatDateShort(postponedUntil)}`;
	}

	const reasonLabel = getStatusReasonLabel(
		payload?.statusLossReason ??
			payload?.lossReason ??
			payload?.statusPostponedReason ??
			payload?.postponedReason ??
			statusItem?.newValues?.lossReason ??
			statusItem?.newValues?.postponedReason ??
			statusItem?.payloadJson?.statusLossReason ??
			statusItem?.payloadJson?.statusPostponedReason,
	);

	const lines = [
		{
			id: 1,
			text,
			...(cross && cross !== text ? { cross } : {}),
		},
	];

	if (reasonLabel) {
		lines.push({
			id: 2,
			text: reasonLabel,
			muted: true,
		});
	}

	return {
		id: 'status',
		title: SECTION_TITLES.status,
		status: {
			lines,
		},
	};
};

const mapObjectionTypo = (objection, index, context) => {
	const isRemoved = objection.solved === true && Boolean(objection.id);

	return {
		id: `objection-${objection.id ?? index}`,
		title: getObjectionTypeName(
			context.objectionTypeById,
			objection.typeId,
		),
		chip: isRemoved ? CHIP.removed : CHIP.created,
		texts: objection.comment
			? [
				{
					id: 1,
					text: objection.comment,
					...(isRemoved ? { isThrough: true } : {}),
				},
			]
			: [],
	};
};

const mapPersonalContactTypo = (contact, index) => {
	const title = [contact.lastName, contact.firstName, contact.middleName]
		.filter(Boolean)
		.join(' ');

	const numbers = (contact.numbers || [])
		.map((number) => {
			if (typeof number === 'string') return number;

			return number?.number ?? number?.text;
		})
		.filter(Boolean);

	const options = [
		contact.position && {
			id: 1,
			label: 'Должность',
			value: contact.position,
		},
		numbers.length && {
			id: 2,
			label: 'Телефон',
			value: numbers.join('\n'),
		},
		contact.comment && {
			id: 3,
			label: 'Комментарий',
			value: contact.comment,
		},
		contact.responsiblePerson !== undefined && {
			id: 4,
			label: 'Лицо, принимающее решения',
			value: contact.responsiblePerson ? 'Да' : 'Нет',
		},
	].filter(Boolean);

	return {
		id: `contact-${contact.id ?? index}`,
		title: title || 'Контакт',
		chip: contact.id ? CHIP.changed : CHIP.created,
		...(options.length ? { options } : {}),
	};
};

const mapGeneralNumberTypo = (number, index, numberItem) => {
	const oldValues = numberItem?.oldValues ?? {};
	const options = [];
	const nextPhone = number.number ?? '';
	const prevPhone = oldValues.number ?? '';

	if (number.id && prevPhone && nextPhone && prevPhone !== nextPhone) {
		options.push({
			id: 1,
			label: 'Телефон',
			value: nextPhone,
			old: prevPhone,
		});
	} else if (nextPhone && (!number.id || !prevPhone)) {
		options.push({
			id: 1,
			label: 'Телефон',
			value: nextPhone,
		});
	}

	const nextComment = number.comment ?? '';
	const prevComment = oldValues.comment ?? '';

	if (number.id && prevComment !== nextComment && (prevComment || nextComment)) {
		options.push({
			id: 2,
			label: 'Комментарий',
			value: nextComment,
			...(prevComment ? { old: prevComment } : {}),
		});
	} else if (!number.id && nextComment) {
		options.push({
			id: 2,
			label: 'Комментарий',
			value: nextComment,
		});
	}

	const nextActive = number.active;
	const prevActive = oldValues.active;

	if (
		number.id &&
		prevActive !== undefined &&
		nextActive !== undefined &&
		Boolean(prevActive) !== Boolean(nextActive)
	) {
		options.push({
			id: 3,
			label: 'Статус',
			value: CONTACT_STATUS_LABEL[Boolean(nextActive)],
			old: CONTACT_STATUS_LABEL[Boolean(prevActive)],
		});
	}

	return {
		id: `number-${number.id ?? index}`,
		title: nextPhone || prevPhone || 'Номер',
		chip: number.id ? CHIP.changed : CHIP.created,
		...(options.length ? { options } : {}),
	};
};

const mapContractTypo = (contract, index) => ({
	id: `contract-${contract.id ?? index}`,
	title: contract.name ?? 'Договор',
	chip: CHIP.uploaded,
	options: [
		contract.file?.name && {
			id: 1,
			label: 'Файл',
			value: contract.file.name,
		},
		contract.name &&
			!contract.file?.name && {
			id: 1,
			label: 'Файл',
			value: contract.name,
		},
		contract.signed !== undefined && {
			id: 2,
			label: 'Договор подписан',
			value: contract.signed ? 'Да' : 'Нет',
		},
	].filter(Boolean),
});

const mapDocumentTypo = (document, index) => ({
	id: `document-${document.id ?? index}`,
	title: document.name ?? 'Документ',
	chip: CHIP.uploaded,
	options: [
		document.file?.name
			? { id: 1, label: 'Файл', value: document.file.name }
			: document.name && { id: 1, label: 'Файл', value: document.name },
	].filter(Boolean),
});

const normalizeStringList = (list) =>
	(list ?? []).map((item) => String(item).trim()).filter(Boolean);

const pushListFieldChanges = (
	{ added, changed, removed },
	{ id, label, oldList, newList },
) => {
	const oldValues = normalizeStringList(oldList);
	const newValues = normalizeStringList(newList);

	if (isEqual(oldValues, newValues)) return;

	const addedItems = difference(newValues, oldValues);
	const removedItems = difference(oldValues, newValues);

	if (addedItems.length && removedItems.length) {
		const changeCount = Math.min(addedItems.length, removedItems.length);
		const changedAdded = addedItems.slice(0, changeCount);
		const changedRemoved = removedItems.slice(0, changeCount);
		const onlyAdded = addedItems.slice(changeCount);
		const onlyRemoved = removedItems.slice(changeCount);

		changed.push({
			id,
			label,
			value: changedAdded.join(' • '),
			old: changedRemoved.join(' • '),
		});

		if (onlyAdded.length) {
			added.push({
				id,
				label,
				value: onlyAdded.join(' • '),
			});
		}

		if (onlyRemoved.length) {
			removed.push({
				id,
				label,
				old: onlyRemoved.join(' • '),
			});
		}

		return;
	}

	if (addedItems.length) {
		added.push({
			id,
			label,
			value: addedItems.join(' • '),
		});
	}

	if (removedItems.length) {
		removed.push({
			id,
			label,
			old: removedItems.join(' • '),
		});
	}
};

const mapCompanyParamsTypo = (params, paramsItem) => {
	if (!params) return null;

	const oldValues = paramsItem?.oldValues ?? {};
	const added = [];
	const changed = [];
	const removed = [];
	const oldDate = oldValues.knownFrom ?? oldValues.openingDate ?? null;
	const newDate = params.knownFrom ?? params.openingDate ?? null;

	if (oldDate && newDate && oldDate !== newDate) {
		changed.push({
			id: 1,
			label: 'Известен с',
			value: formatKnownFromDate(newDate),
			old: formatKnownFromDate(oldDate),
		});
	} else if (!oldDate && newDate) {
		added.push({
			id: 1,
			label: 'Известен с',
			value: formatKnownFromDate(newDate),
		});
	} else if (oldDate && !newDate) {
		removed.push({
			id: 1,
			label: 'Известен с',
			old: formatKnownFromDate(oldDate),
		});
	}

	pushListFieldChanges(
		{ added, changed, removed },
		{
			id: 2,
			label: 'Эл. почта',
			oldList: oldValues.email,
			newList: params.email,
		},
	);

	pushListFieldChanges(
		{ added, changed, removed },
		{
			id: 3,
			label: 'Ссылки',
			oldList: oldValues.sitesExternal ?? oldValues.site,
			newList: params.sitesExternal ?? params.site,
		},
	);

	const typos = [
		added.length && {
			id: 'company-params-added',
			chip: CHIP.added,
			options: added,
		},
		changed.length && {
			id: 'company-params-changed',
			chip: CHIP.changed,
			options: changed,
		},
		removed.length && {
			id: 'company-params-removed',
			chip: CHIP.deleted,
			options: removed,
		},
	].filter(Boolean);

	return typos.length ? typos : null;
};

const normalizeCompanyIds = (ids = [], context = {}) => {
	const companyId = context.companyId ? String(context.companyId) : null;

	return ids.map((id) => String(id)).filter((id) => id && id !== companyId);
};

const mapCompanyGroupTypo = (ids, { chip, context, id }) => {
	const labels = normalizeCompanyIds(ids, context).map((companyId) =>
		getCompanyAreaLabel(context.companyById, companyId),
	);

	if (!labels.length) return null;

	return {
		id,
		title: pluralizeAreas(labels.length),
		chip,
		options: labels.map((value, index) => ({
			id: index + 1,
			value,
		})),
	};
};

const mapCompanyGroupLinksTypos = (companyGroupLinks, context) => {
	if (!companyGroupLinks) return [];

	return [
		mapCompanyGroupTypo(companyGroupLinks.link, {
			chip: CHIP.created,
			context,
			id: 'company-group-link',
		}),
		mapCompanyGroupTypo(companyGroupLinks.unlink, {
			chip: CHIP.removed,
			context,
			id: 'company-group-unlink',
		}),
	].filter(Boolean);
};

const mapMeetingReason = (meeting, index) => ({
	id: `meeting-${index}`,
	label: 'Встреча',
	date: meeting.date,
	from: meeting.time,
	to: meeting.place,
});

const mapMessagingReason = (messaging, index) => ({
	id: `messaging-${index}`,
	label: 'Переписка',
	date: messaging.date,
});

const mapCallReason = (callId, index) => ({
	id: `call-${callId ?? index}`,
	textOnly: `Звонок #${callId}`,
});

const buildPayloadFromItems = (items = []) => {
	const payload = {};

	for (const item of items) {
		const data = {
			...(item.payloadJson ?? {}),
			...(item.newValues ?? {}),
		};

		switch (item.entityType) {
			case 'objections':
				payload.objections = [...(payload.objections ?? []), data];
				break;
			case 'personal_contacts':
				payload.personalContacts = [
					...(payload.personalContacts ?? []),
					data,
				];
				break;
			case 'company_general_numbers':
				payload.generalNumbers = [
					...(payload.generalNumbers ?? []),
					data,
				];
				break;
			case 'contracts':
				payload.contracts = [...(payload.contracts ?? []), data];
				break;
			case 'documents':
				payload.documents = [...(payload.documents ?? []), data];
				break;
			case 'company_params':
				payload.companyParams = data;
				break;
			case 'companies':
				payload.companyStatusId = data.companyStatusId ?? data.statusId;
				payload.statusPostponedUntil =
					data.statusPostponedUntil ?? data.postponedUntil;
				payload.statusPostponedReason =
					data.statusPostponedReason ?? data.postponedReason;
				payload.statusLossReason =
					data.statusLossReason ?? data.lossReason;
				break;
			case 'meetings':
				payload.meetings = [...(payload.meetings ?? []), data];
				break;
			case 'messaging':
				payload.messagings = [...(payload.messagings ?? []), data];
				break;
			case 'calls':
				payload.calls = {
					attach: [
						...(payload.calls?.attach ?? []),
						...(data.callId ? [data.callId] : []),
					],
				};
				break;
			case 'worths':
				payload.worth = data;
				break;
			case 'company_group_links': {
				payload.companyGroupLinks = payload.companyGroupLinks || {};

				if (item.operationType === 'link') {
					const linkIds = Array.isArray(item.payloadJson)
						? item.payloadJson
						: item.newValues?.link;

					if (linkIds?.length) {
						payload.companyGroupLinks.link = linkIds;
					}
				}

				if (item.operationType === 'unlink') {
					const unlinkIds = Array.isArray(item.payloadJson)
						? item.payloadJson
						: item.newValues?.unlink;

					if (unlinkIds?.length) {
						payload.companyGroupLinks.unlink = unlinkIds;
					}
				}

				break;
			}
			default:
				break;
		}
	}

	return payload;
};

const hasPayloadActions = (payload = {}) =>
	Boolean(
		payload.companyStatusId ||
			payload.statusPostponedUntil ||
			payload.objections?.length ||
			payload.personalContacts?.length ||
			payload.generalNumbers?.length ||
			payload.contracts?.length ||
			payload.documents?.length ||
			payload.companyParams ||
			payload.meetings?.length ||
			payload.messagings?.length ||
			payload.calls?.attach?.length ||
			payload.worth ||
			payload.companyGroupLinks?.link?.length ||
			payload.companyGroupLinks?.unlink?.length,
	);

const buildSectionsFromPayload = (payload, items, context) => {
	const sections = [];
	const itemsByType = indexItemsByEntityType(items);

	const statusSection = mapStatusSection(payload, itemsByType, context);
	if (statusSection) sections.push(statusSection);

	const reasons = [
		...(payload?.meetings ?? []).map(mapMeetingReason),
		...(payload?.messagings ?? []).map(mapMessagingReason),
		...(payload?.calls?.attach ?? []).map(mapCallReason),
	];

	if (reasons.length) {
		const section = ensureSection(sections, 'reasons');
		section.reasons = reasons;
	}

	if (payload?.objections?.length) {
		const section = ensureSection(sections, 'objections');
		section.typos = payload.objections.map((objection, index) =>
			mapObjectionTypo(objection, index, context),
		);
	}

	const generalNumberItems = itemsByType.get('company_general_numbers') ?? [];
	const contacts = [
		...(payload?.personalContacts ?? []).map((contact, index) =>
			mapPersonalContactTypo(contact, index),
		),
		...(payload?.generalNumbers ?? []).map((number, index) =>
			mapGeneralNumberTypo(number, index, generalNumberItems[index]),
		),
	];

	if (contacts.length) {
		const section = ensureSection(sections, 'contacts');
		section.typos = contacts;
	}

	const documents = [
		...(payload?.contracts ?? []).map((contract, index) =>
			mapContractTypo(contract, index),
		),
		...(payload?.documents ?? []).map((document, index) =>
			mapDocumentTypo(document, index),
		),
	];

	if (documents.length) {
		const section = ensureSection(sections, 'documents');
		section.typos = documents;
	}

	const paramsTypos = mapCompanyParamsTypo(
		payload?.companyParams,
		itemsByType.get('company_params')?.[0],
	);

	if (paramsTypos?.length) {
		const section = ensureSection(sections, 'company_params');
		section.typos = paramsTypos;
	}

	const groupLinkTypos = mapCompanyGroupLinksTypos(
		payload?.companyGroupLinks,
		context,
	);

	if (groupLinkTypos.length) {
		const section = ensureSection(sections, 'company_group_links');
		section.typos = groupLinkTypos;
	}

	const hasActions =
		payload?.objections?.length ||
		payload?.personalContacts?.length ||
		payload?.generalNumbers?.length ||
		payload?.contracts?.length ||
		payload?.documents?.length ||
		payload?.companyParams ||
		payload?.worth ||
		groupLinkTypos.length;

	if (!statusSection && hasActions) {
		sections.unshift({
			id: 'status-default',
			title: SECTION_TITLES.status,
			status: {
				lines: [{ id: 1, text: 'Без изменений' }],
			},
		});
	}

	if (hasActions && !reasons.length) {
		const section = ensureSection(sections, 'reasons');
		section.reasons = [{ id: 'no-reason', textOnly: 'Без основания' }];
	}

	return sections;
};

export const mapActivityToEventCard = (activity, context = {}) => {
	const version = activity.lastVersion ?? {};
	const items = (version.items ?? []).map((item) => ({
		...item,
		entityType: normalizeEnum(item.entityType),
		role: normalizeEnum(item.role),
		operationType: normalizeEnum(item.operationType),
		oldValues: parseJson(item.oldValues),
		newValues: parseJson(item.newValues),
		payloadJson: parseJson(item.payloadJson),
	}));

	let payload = parseJson(version.payloadJson) ?? {};

	if (!hasPayloadActions(payload) && items.length) {
		payload = { ...payload, ...buildPayloadFromItems(items) };
	}

	const sections = buildSectionsFromPayload(payload, items, context);

	const rightLines = [];

	if (activity.canUpdate) {
		rightLines.push({
			id: 1,
			text: 'Исправить ошибку',
			link: { to: `?create=true&edit=${activity.id}` },
			visibleUntil: activity.updateUntil,
		});

		if (activity.updateUntil) {
			rightLines.push({
				id: 2,
				countdownUntil: activity.updateUntil,
				typo: { color: 'primary' },
			});
		}
	}

	return {
		id: activity.id,
		description: activity.comment,
		head: {
			leftLines: [
				{ id: 1, text: activity.createdBy?.name ?? '—' },
				{ id: 2, text: formatDateShort(activity.createdAt) },
			],
			...(rightLines.length ? { rightLines } : {}),
		},
		sections,
	};
};

export const mapActivitiesToEventCards = (activities, context = {}) => {
	return activities.map((activity) =>
		mapActivityToEventCard(activity, context),
	);
};

const pushCompanyIds = (ids, target) => {
	if (!ids) return;

	const list = Array.isArray(ids) ? ids : [ids];

	for (const id of list) {
		if (id != null && id !== '') {
			target.add(String(id));
		}
	}
};

export const collectCompanyIdsFromActivities = (activities = []) => {
	const ids = new Set();

	for (const activity of activities) {
		const version = activity.lastVersion ?? {};
		const payload = parseJson(version.payloadJson) ?? {};

		pushCompanyIds(payload.companyGroupLinks?.link, ids);
		pushCompanyIds(payload.companyGroupLinks?.unlink, ids);

		for (const item of version.items ?? []) {
			if (normalizeEnum(item.entityType) !== 'company_group_links') {
				continue;
			}

			const payloadJson = parseJson(item.payloadJson);
			const newValues = parseJson(item.newValues);

			if (Array.isArray(payloadJson)) {
				pushCompanyIds(payloadJson, ids);
			}

			pushCompanyIds(newValues?.link, ids);
			pushCompanyIds(newValues?.unlink, ids);
		}
	}

	return [...ids];
};

const toMapById = (items = []) =>
	Object.fromEntries(items.map((item) => [item.id, item]));

export const createActivityMapperContext = ({
	statuses = [],
	objectionTypes = [],
	companyById = {},
	companyId = null,
} = {}) => ({
	statusById: toMapById(statuses),
	objectionTypeById: toMapById(objectionTypes),
	companyById,
	companyId,
});
